import { toast } from "sonner";
import { Lightbulb } from "lucide-react";
import MissionShell from "../MissionShell";
import { Card, Field } from "@/components/kit";
import { ANGLES } from "../content";
import { useProgress } from "../store";
import { isDone, REQUIREMENTS, filled } from "../logic";

const FIELDS = [
  ["audience", "À qui s'adresse mon produit ?", "Ex. : les personnes qui bougent beaucoup dans la journée"],
  ["situation", "Dans quelle situation vais-je l'utiliser ?", "Ex. : le matin, en préparant mon sac"],
  ["show", "Que vais-je montrer ?", "Les gestes que l'on verra à l'écran"],
  ["benefit", "Quel bénéfice concret vais-je présenter ?", "Un bénéfice visible, que tu constates toi-même"],
  ["idea", "Quelle est l'idée principale ?", "En une phrase courte"],
];

export function conceptSummary(m3, productName) {
  const angle = ANGLES.find((a) => a.id === m3.angle);
  const parts = [];
  if (angle || filled(productName)) parts.push(`Une vidéo${angle ? ` « ${angle.label} »` : ""}${filled(productName) ? ` autour de ${productName.trim()}` : ""}.`);
  if (filled(m3.audience)) parts.push(`Pour : ${m3.audience.trim()}.`);
  if (filled(m3.situation)) parts.push(`Situation : ${m3.situation.trim()}.`);
  if (filled(m3.show)) parts.push(`Je montre : ${m3.show.trim()}.`);
  if (filled(m3.benefit)) parts.push(`Bénéfice concret : ${m3.benefit.trim()}.`);
  return parts.map((p) => p.replace(/\.\.$/, "."));
}

export default function Mission3() {
  const { state, update, setCompleted } = useProgress();
  const c = state.m3;
  const set = (k) => (v) => update("m3", { [k]: v });
  const summary = conceptSummary(c, state.m2.name);
  return (
    <MissionShell id={3} done={isDone(state, 3)} validate={{ missing: REQUIREMENTS[3](state), onClick: () => { setCompleted(3, true); toast.success("Mission 03 validée !"); } }}>
      <div className="space-y-5">
        <Card>
          <p className="text-[15px] font-medium text-ink">Choisis ton angle</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-3" role="radiogroup" aria-label="Angle de la vidéo">
            {ANGLES.map((a) => {
              const on = c.angle === a.id;
              return (
                <button key={a.id} type="button" role="radio" aria-checked={on} onClick={() => set("angle")(a.id)} data-testid={`angle-${a.id}`}
                  className={`rounded-2xl border p-4 text-left transition-[border-color,background-color,transform] duration-200 hover:-translate-y-0.5 ${on ? "border-bx bg-bx text-white" : "border-line bg-white hover:border-bx/30"}`}>
                  <span className={`font-display text-lg ${on ? "text-white" : "text-bx"}`}>{a.label}</span>
                  <span className={`mt-1 block text-sm ${on ? "text-white/80" : "text-soft"}`}>{a.desc}</span>
                </button>
              );
            })}
          </div>
        </Card>
        <Card className="space-y-5">
          {FIELDS.map(([k, label, ph]) => <Field key={k} label={label} value={c[k]} onChange={set(k)} placeholder={ph} multiline rows={2} testId={`concept-${k}-input`} />)}
          <p className="text-xs text-soft">Enregistrement automatique dans ton compte.</p>
        </Card>
        <div className="rounded-[20px] border border-gold/40 bg-white p-6 sm:p-7" data-testid="concept-summary-card">
          <div className="flex items-center gap-3"><Lightbulb className="h-5 w-5 text-bx" strokeWidth={1.5} /><p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9A7A45]">Mon concept</p></div>
          <p className="mt-4 font-display text-2xl italic text-bx">{filled(c.idea) ? `« ${c.idea.trim()} »` : "Ton idée principale apparaîtra ici"}</p>
          {summary.length > 0 && <ul className="mt-4 space-y-1.5 text-ink/85">{summary.map((s) => <li key={s}>{s}</li>)}</ul>}
        </div>
      </div>
    </MissionShell>
  );
}
